import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import { AppState } from '../store';
import { getFilteredPayrollReportSummaries } from 'src/app/shared/utils';
import { CategoryFilterTab } from 'src/app/shared/components/category-filter-tabs/category-filter-tabs.component';
import {
    ApprovePayrollReportSummary,
    LoadAllPayrollReportSummaries,
    LoadAllPayrollReportSummariesFailure,
    LoadAllPayrollReportSummariesSuccess,
    PayrollReportSummaryActionFailure,
    PayrollReportSummaryActionSuccess,
    RejectPayrollReportSummary
} from '../actions/payroll-report-summary.actions';
import {
    PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER,
    PayrollReportSummaryFilterLabels
} from 'src/app/shared/constants-and-enums/payroll-report-summary-types';
import { PayrollReportSummaryDTO } from '@iota/inh-core-v1-management-client';

export interface PayrollReportSummaryState extends EntityState<PayrollReportSummaryDTO> {
    loading: boolean;
    error: any;

    actionLoading: boolean;
    actionError: any;
}

function selectPayrollReportSummaryId(p: PayrollReportSummaryDTO): string {
    return p.id;
}

function sortPayrollReportSummaries(a: PayrollReportSummaryDTO, b: PayrollReportSummaryDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const payrollReportSummaryAdapter: EntityAdapter<PayrollReportSummaryDTO> = createEntityAdapter<PayrollReportSummaryDTO>({
    selectId: selectPayrollReportSummaryId,
    sortComparer: sortPayrollReportSummaries
});

export const initialState: PayrollReportSummaryState = payrollReportSummaryAdapter.getInitialState({
    loading: false,
    error: undefined,

    actionLoading: false,
    actionError: undefined
});

export const payrollReportSummaryReducer = createReducer(
    initialState,

    on(LoadAllPayrollReportSummaries, (state) => ({ ...state, loading: true })),
    on(LoadAllPayrollReportSummariesSuccess, (state, { payrollReports }) =>
        payrollReportSummaryAdapter.setAll(payrollReports, { ...state, loading: false, error: undefined })
    ),
    on(LoadAllPayrollReportSummariesFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(ApprovePayrollReportSummary, RejectPayrollReportSummary, (state) => ({
        ...state,
        actionError: undefined,
        actionLoading: true
    })),
    on(PayrollReportSummaryActionSuccess, (state, { payrollReport }) =>
        payrollReportSummaryAdapter.upsertOne(payrollReport, { ...state, actionLoading: false, actionError: undefined })
    ),
    on(PayrollReportSummaryActionFailure, (state, { error }) => ({ ...state, actionLoading: false, actionError: error }))
);

const { selectAll } = payrollReportSummaryAdapter.getSelectors();

export const payrollReportSummaryState = (state: AppState) => state.payrollReportSummaryState;
export const _payrollReportSummaries = createSelector(payrollReportSummaryState, selectAll);
export const payrollReportSummaryById = (id: string) =>
    createSelector(_payrollReportSummaries, (allSummaries) => allSummaries?.find((summary) => summary.id === id));
export const payrollReportSummariesLoading = createSelector(payrollReportSummaryState, (state) => state.loading);
export const payrollReportSummariesError = createSelector(payrollReportSummaryState, (state) => state.error);
export const payrollReportSummaryActionLoading = createSelector(payrollReportSummaryState, (state) => state.actionLoading);
export const payrollReportSummaryActionError = createSelector(payrollReportSummaryState, (state) => state.actionError);

export const payrollReportSummaryOverviewFilterTabs = createSelector(_payrollReportSummaries, (summaries) => {
    const enumKeyAndValues = Object.values(PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER);
    const enumValues = enumKeyAndValues.filter((value) => isNaN(Number(value)));

    return enumValues?.map((key) => {
        const filteredPayrollReportSummaries = getFilteredPayrollReportSummaries(summaries, key);

        const result: CategoryFilterTab = {
            key,
            label: PayrollReportSummaryFilterLabels?.[key],
            count: filteredPayrollReportSummaries?.length
        };

        return result;
    });
});
